/*
 * Copyright 2023 Google LLC
 *
 * This library is free software; you can redistribute it and/or
 * modify it under the terms of the GNU Lesser General Public
 * License as published by the Free Software Foundation; either
 * version 2.1 of the License, or (at your option) any later version.
 *
 * This library is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the GNU
 * Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public
 * License along with this library; if not, write to the Free Software
 * Foundation, Inc., 51 Franklin Street, Fifth Floor, Boston, MA 02110-1301, USA
 */
import initModule from './libapi.mjs'
// A helper that allows to distinguish critical errors from library errors.
export function rethrowIfCritical(err) {
  // If it's precisely Error, it's a custom error; anything else - SyntaxError,
  // WebAssembly.RuntimeError, TypeError, etc. - is treated as critical here.
  if (err?.constructor !== Error) {
    throw err
  }
}
/**
 * Turn a bare WASM exception pointer back into the error it was.
 *
 * Under emscripten's -fexceptions, a C++ `std::runtime_error` thrown by the binding
 * (every failing gp_* call: `gpp_try` → `throw std::runtime_error(gp_result_as_string())`)
 * reaches JS as an INTEGER — the exception object's address, different on every throw.
 * On builds linked with EXPORT_EXCEPTION_HANDLING_HELPERS (v2) the module can read the
 * type and message back out of that pointer; here that becomes a real Error carrying the
 * libgphoto2 message ("Bad parameters", "Could not claim the USB device", …) plus the
 * pointer for reference. Anything else — a build without the helper (v1), a value that
 * is not a pointer, a pointer that will not decode — is returned untouched.
 *
 * @param {any} Module the emscripten module instance (may be undefined)
 * @param {unknown} raw the caught value
 */
function decodeWasmException(Module, raw) {
  if (typeof raw !== 'number' || !Module || typeof Module.getExceptionMessage !== 'function') {
    return raw
  }
  let info
  try {
    info = Module.getExceptionMessage(raw)
  } catch {
    return raw
  }
  const [type, message] = Array.isArray(info) ? info : [String(info ?? ''), '']
  const err = new Error(message || type || `wasm exception ${raw}`)
  err.name = type || 'WasmException'
  err.wasmPointer = raw
  try {
    // We consumed the exception here; let the runtime free it. Best-effort — a leak of
    // one small object on a rare error path is preferable to any risk on this path.
    Module.decrementExceptionRefcount?.(raw)
  } catch {
    // ignore
  }
  return err
}
const INTERFACE_CLASS = 6 // PTP
const INTERFACE_SUBCLASS = 1 // MTP
/**
 * One emscripten module instance PER CAMERA SLOT (not one global singleton).
 *
 * The upstream library keeps a single module-level ModulePromise, so every Camera
 * instance shares ONE WASM memory and ONE libusb/libgphoto2 state. That is fine for
 * one camera, but constructing a second Module.Context() for a dual-camera booth
 * runs camera init inside the same libusb context that is already mid-liveview on
 * the first body — corrupting shared state and crashing the WASM
 * ("memory access out of bounds" in doRewind / "Cannot use deleted val"; observed
 * with two Canon R100 bodies, 2026-07-15).
 *
 * initModule() is an emscripten MODULARIZE factory: each call creates a fully
 * isolated instance (own memory, own libusb). Keying instances by camera slot gives
 * the primary and secondary bodies independent driver states, while reconnects on
 * the SAME slot keep reusing that slot's instance — so single-camera booths still
 * run exactly one WASM instance for the app's lifetime, same as before.
 */
const modulePromisesBySlot = new Map()
/**
 * Two builds of the WASM camlib can be bundled side by side:
 *   v1 — the long-standing build (libgphoto2 2.5.28): 2000ms autofocus ceiling inside
 *        every capture, and no way to capture without that autofocus.
 *   v2 — rebuilt from upstream on the 2.5.31.1 emscripten fork: 500ms ceiling, EOS
 *        R100/R50 in the model table, and (on builds that carry it) captureImageNoAf().
 * The variant is chosen once per Camera at construction, from the `wasm_gphoto2_v2`
 * flag; instances are keyed by variant AND slot so a flag flip mid-run never hands a
 * v2 context to a v1 caller. v1 stays the default for every booth.
 */
const V2_LOADER = () => import('../web-gphoto2-v2/libapi.mjs').then((m) => m.default())
export class Camera {
  #queue = Promise.resolve()
  #context = null
  #moduleSlot
  #variant
  #module = null
  /**
   * The gp_result_as_string message behind the most recent opaque WASM exception
   * (v2 only). A failing gp_* call throws a std::runtime_error that reaches JS as a
   * bare pointer — a different integer each throw — so the number alone is useless;
   * this is that number decoded. uniCamUtils logs it alongside the failure.
   * @type {string | null}
   */
  lastWasmError = null
  /**
   * @param {{ moduleSlot?: string, variant?: 'v1' | 'v2' }} [options] moduleSlot picks
   * the WASM instance; cameras that must run concurrently need DIFFERENT slots.
   * Defaults to 'primary' (the shared legacy instance) on the v1 build.
   */
  constructor(options = {}) {
    this.#moduleSlot = options.moduleSlot || 'primary'
    this.#variant = options.variant === 'v2' ? 'v2' : 'v1'
  }
  /** Which WASM build this instance runs on ('v1' | 'v2'). */
  get variant() {
    return this.#variant
  }
  static async showPicker() {
    // @ts-ignore navigator guaranteed exist
    await navigator.usb.requestDevice({
      filters: [
        {
          classCode: INTERFACE_CLASS,
          subclassCode: INTERFACE_SUBCLASS
        }
      ]
    })
  }
  /** Bind to the camera. */
  async connect() {
    const key = `${this.#variant}:${this.#moduleSlot}`
    let modulePromise = modulePromisesBySlot.get(key)
    if (!modulePromise) {
      modulePromise = this.#variant === 'v2' ? V2_LOADER() : initModule()
      modulePromisesBySlot.set(key, modulePromise)
    }
    let Module
    try {
      Module = await modulePromise
      // NOT bounded by a JS timer. A connect that is merely SLOW still completes, and
      // abandoning it used to leave the init running inside this WASM instance still
      // holding the USB device: the caller's recovery then reset and closed that device
      // underneath the abandoned init, and every later transfer from that zombie threw
      // "The transfer was cancelled" and then "The device must be opened first" forever
      // (field, 2026-09-16 — 4921 console errors from one switch).
      // A connect that is genuinely HUNG blocks the renderer's main thread inside WASM,
      // where no JS timer can fire at all, so a timer here could never have helped that
      // case either. Recovery for a blocked thread lives in the main process
      // (rendererHangWatchdog), which is the only code still running when it happens.
      const settled = { ctx: await Promise.resolve(new Module.Context()) }
      this.#context = settled.ctx
      this.#module = Module
    } catch (raw) {
      // gp_camera_init failing ("Could not claim the USB device", "Unknown model", …)
      // arrives as a pointer too — decode it so the operator sees the reason, and so it
      // is classified as the library-level error it is (see below).
      const err = decodeWasmException(Module, raw)
      // A critical failure (WebAssembly.RuntimeError, failed module load, deleted
      // emvals) means this slot's WASM instance is unusable — evict it so the next
      // reconnect builds a FRESH instance instead of reusing a bricked one. Plain
      // `Error` is a library-level error (e.g. no camera found) on a healthy module.
      // A TIMEOUT is evicted too: the init we abandoned may still be running inside that
      // instance and holding the device, so reusing it would hang the retry the same way.
      if (err?.constructor !== Error) {
        modulePromisesBySlot.delete(`${this.#variant}:${this.#moduleSlot}`)
      }
      throw err
    }
  }
  async #schedule(op) {
    // The field bug this guards against (2026-09-15): within one session EVERY camera op
    // failed with the IDENTICAL pointer (e.g. 1127976 ×6). A fresh throw has a fresh
    // address, so the later ops never ran. What happened: the first op was a probe of a
    // Canon-only widget on a Sony — a harmless, expected "Bad parameters" — but it
    // surfaced as a bare integer, `rethrowIfCritical` read "not an Error" as a WASM
    // crash and poisoned #queue, and every later op (including the ordinary capture
    // that works on that body) short-circuited on the rejected queue. Decoding FIRST
    // turns a library error back into a plain Error, so the queue continues and the next
    // op reaches the camera. Only a genuine crash — WebAssembly.RuntimeError, or a value
    // that will not decode (v1 has no decoder, so v1 behaves exactly as before) — still
    // poisons the queue.
    const res = this.#queue
      .then(() => op(this.#context))
      .catch((raw) => {
        const err = decodeWasmException(this.#module, raw)
        if (err !== raw) {
          this.lastWasmError = err.message
          console.error(`[web-gphoto2 v2] ${err.name}: ${err.message} (ptr ${raw})`)
        }
        throw err
      })
    this.#queue = res.catch(rethrowIfCritical)
    return res
  }
  async disconnect() {
    if (this.#context && !this.#context.isDeleted()) {
      // The Context destructor runs gp_camera_exit — the camera's goodbye (Canon:
      // SetRemoteMode(1) + CloseSession). Under asyncify that may complete after this
      // call returns; when the binding hands back a promise, wait for it so callers
      // that must finish before the process dies (the app-close path) actually do.
      const done = this.#context.delete()
      if (done && typeof done.then === 'function') {
        await done
      }
    }
    // v2 (libusb 1.0.27's own WebUSB backend) keeps device state at module level, so a
    // fresh Context on the same instance can inherit a handle the browser has already
    // closed: seen in the field as hundreds of "controlTransferIn: the device must be
    // opened first" followed by "Could not detect any camera" on every recovery
    // attempt. Drop the instance on disconnect so the next connect() starts libusb
    // from scratch. Costs one WASM instantiation (~1s) per reconnect; v1 is unchanged.
    if (this.#variant === 'v2') {
      modulePromisesBySlot.delete(`${this.#variant}:${this.#moduleSlot}`)
    }
  }
  /**
   * Drop this slot's cached WASM instance so the next connect() builds a fresh one.
   * Used by the reconnect self-heal: a retry inside a module whose libusb still thinks
   * it owns the device just hangs again.
   */
  discardModule() {
    modulePromisesBySlot.delete(`${this.#variant}:${this.#moduleSlot}`)
  }
  async getConfig() {
    return this.#schedule((context) => context.configToJS())
  }
  async getSupportedOps() {
    if (this.#context) {
      return await this.#context.supportedOps()
    }
    throw new Error('You need to connect to the camera first')
  }
  /**
   * @param {string} name
   * @param {string|number|boolean} value
   * @param {{ settle?: boolean }} [options] `settle: false` skips the 800ms UI-settle
   *   wait below. Only for writes whose value is never read back — an ACTION widget
   *   such as `autofocusdrive`, where the settle is pure latency on the capture path.
   */
  async setConfigValue(name, value, options = {}) {
    const settle = options.settle !== false
    let uiTimeout
    await this.#schedule((context) => {
      // This is terrible, yes... but some configs return too quickly before they're actually updated.
      // We want to wait some time before updating the UI in that case, but not block subsequent ops.
      if (settle) uiTimeout = new Promise((resolve) => setTimeout(resolve, 800))
      return context.setConfigValue(name, value)
    })
    if (uiTimeout) await uiTimeout
  }
  async capturePreviewAsBlob() {
    return this.#schedule((context) => context.capturePreviewAsBlob())
  }
  async captureImageAsFile() {
    return this.#schedule((context) => context.captureImageAsFile())
  }
  /**
   * Press without autofocus, then download (v2 builds that carry the binding). Throws
   * a plain Error on builds without it so callers fall back to captureImageAsFile().
   * @param {number} [timeoutMs]
   */
  async captureImageNoAf(timeoutMs = 10000) {
    return this.#schedule((context) => {
      if (typeof context.captureImageNoAf !== 'function') {
        throw new Error('captureImageNoAf is not available on this WASM build')
      }
      return context.captureImageNoAf(timeoutMs)
    })
  }
  async consumeEvents() {
    return this.#schedule((context) => context.consumeEvents())
  }
}
